import { Button, styled } from "@mui/material";
import React, {
  forwardRef,
  ReactNode,
  useImperativeHandle,
  useState,
} from "react";
import { useDetectWidthChange } from "./useDetectWidthChange.tsx";
import { useNavItemProvider } from "./NavItemProvider.tsx";

export const ExpandableText = forwardRef<
  { click: () => void },
  {
    associatedNavTitle: string;
    preText?: ReactNode[];
    mainText?: ReactNode;
    postText?: ReactNode[];
    onClick: (string) => void;
    animate?: boolean;
  }
>((props, ref) => {
  const { setClickedNavItem } = useNavItemProvider();
  const {
    preText,
    mainText,
    postText,
    onClick,
    animate = true,
    associatedNavTitle,
  } = props;
  const [clicked, setClicked] = useState(false);

  const [preTextVisible, setPreTextVisible] = useState(0);
  const [postTextVisible, setPostTextVisible] = useState(0);
  const [navShown, setNavShown] = useState(false);

  const resetState = () => {
    setClicked(false);
    setPreTextVisible(0);
    setPostTextVisible(0);
  };

  useDetectWidthChange(resetState);

  useImperativeHandle(ref, () => ({
    click: () => handleClick(),
  }));

  const startPreTextCounter = () => {
    let count = 0;
    const interval = setInterval(() => {
      if (!preText || count >= preText.length) {
        clearInterval(interval);
      } else {
        setPreTextVisible((prev) => prev + 1);
        count++;
      }
    }, 350);
  };

  const startPostTextCounter = () => {
    let count = 0;
    const interval = setInterval(() => {
      if (!postText || count >= postText.length) {
        clearInterval(interval);
      } else {
        setPostTextVisible((prev) => prev + 1);
        count++;
      }
    }, 350);
  };

  const handleClick = () => {
    //prevent multiple clicks
    if (clicked) return;

    setClicked(true);
    startPreTextCounter();

    setTimeout(
      () => {
        startPostTextCounter();
      },
      350 * (preText?.length ?? 0),
    );

    setTimeout(
      () => {
        onClick(mainText);
        setNavShown(true);
      },
      350 * (preText?.length ?? 0) + 350 * (postText?.length ?? 0) + 500,
    );
  };

  return (
    <span>
      {preText && (
        <>
          {preText.slice(0, preTextVisible).map((text, index) => {
            return (
              <AnimatedText key={index}>
                {index !== 0 && " "}
                {text}
                {index === preText.length - 1 && " "}
              </AnimatedText>
            );
          })}
        </>
      )}
      <Button
        onClick={
          clicked
            ? () => {
                setClickedNavItem(associatedNavTitle);
              }
            : handleClick
        }
        // disabled={clicked}
        sx={(theme) => ({
          margin: 0,
          padding: 0,
          paddingX: 1,
          userSelect: "text",
          //jump up animation on render
          ...(animate &&
            !navShown && {
              animation: "jump 0.7s forwards",
              animationDelay: "1s",
              animationIterationCount: 2,
              "@keyframes jump": {
                "0%": { transform: "translateY(0)" },
                "15%": { transform: "translateY(-8px)" },
                "50%": { transform: "translateY(3px)" },
                "100%": { transform: "translateY(0)" },
              },
            }),
          //highlight animation on click
          ...(navShown && {
            animation: "highlight 1s forwards",
            //animate from bottom right
            "@keyframes highlight": {
              "0%": { transform: "translateX(0)" },
              "25%": { transform: "translateX(-4px)" },
              "50%": { transform: "translateX(4px)" },
              "75%": { transform: "translateX(-4px)" },
              "100%": { transform: "translateX(0)" },
            },
          }),
          ...(clicked && {
            color: `${theme.palette.text.primary}ff`,
            backgroundColor: `${theme.palette.primary.main}33`,
            borderColor: `${theme.palette.primary.main}dd`,
            fontWeight: "regular",
            borderWidth: 1,
          }),
        })}
        variant="outlined"
      >
        {mainText}
      </Button>

      {postText && (
        <>
          {postText.slice(0, postTextVisible).map((text, index) => {
            return (
              <AnimatedText key={index}>
                {index === 0 && " "}
                {text}
                {index !== postText.length - 1 && " "}
              </AnimatedText>
            );
          })}
        </>
      )}
    </span>
  );
});

const AnimatedText = styled("span")(({ theme }) => ({
  padding: "12px 8px",
  margin: "0 -8px",
  borderRadius: "4px",
  animation: "fadeOut 1s forwards ease-out",
  "@keyframes fadeOut": {
    from: { backgroundColor: `${theme.palette.primary.main}`, opacity: 0.3 },
    to: { backgroundColor: "transparent", opacity: 1 },
  },
}));
